import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Helmet } from 'react-helmet-async'
import { api } from '@/services/api'
import { toast } from 'react-toastify'
import { useState } from 'react'

interface Content {
  id: number
  title: string
  description?: string
  status: string
  createdAt: string
  user: {
    id: number
    username?: string
    email: string
  }
  s3Key?: string
  mimeType?: string
  fileSize?: number
}

const ModerationPage = () => {
  const [selectedContent, setSelectedContent] = useState<Content | null>(null)
  const [rejectionReason, setRejectionReason] = useState('')
  
  const queryClient = useQueryClient()

  const { data: contents, isLoading } = useQuery({
    queryKey: ['contents', 'moderation'],
    queryFn: async () => {
      const response = await api.get('/contents?status=PENDING')
      return response.data['hydra:member'] as Content[]
    },
  })

  const approveMutation = useMutation({
    mutationFn: async (contentId: number) => {
      await api.patch(`/contents/${contentId}`, {
        status: 'APPROVED',
        approvedAt: new Date().toISOString(),
      })
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['contents'] })
      toast.success('Contenu approuvé')
      setSelectedContent(null)
    },
    onError: () => {
      toast.error('Erreur lors de l\'approbation')
    },
  })

  const rejectMutation = useMutation({
    mutationFn: async ({ contentId, reason }: { contentId: number; reason: string }) => {
      await api.patch(`/contents/${contentId}`, {
        status: 'REJECTED',
        rejectionReason: reason,
        rejectedAt: new Date().toISOString(),
      })
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['contents'] })
      toast.success('Contenu rejeté')
      setSelectedContent(null)
      setRejectionReason('')
    },
    onError: () => {
      toast.error('Erreur lors du rejet')
    },
  })

  const handleApprove = (contentId: number) => {
    approveMutation.mutate(contentId)
  }

  const handleReject = (contentId: number) => {
    if (!rejectionReason.trim()) {
      toast.error('Veuillez fournir une raison de rejet')
      return
    }
    rejectMutation.mutate({ contentId, reason: rejectionReason })
  }

  if (isLoading) {
    return <div className="loading">Chargement...</div>
  }

  return (
    <div>
      <Helmet>
        <title>Modération - EdenPlaisir.com</title>
        <meta name="description" content="Panneau de modération des contenus" />
        <meta name="robots" content="noindex, follow" />
      </Helmet>
      
      <h1>Modération</h1>
      
      {contents && contents.length > 0 ? (
        <div className="grid">
          {contents.map((content) => (
            <div key={content.id} className="card">
              <h3>{content.title}</h3>
              {content.description && (
                <p style={{ color: '#ccc', marginBottom: '1rem' }}>
                  {content.description}
                </p>
              )}
              
              <div style={{ marginBottom: '1rem', fontSize: '0.9rem', color: '#999' }}>
                <p><strong>Créateur:</strong> {content.user.username || content.user.email}</p>
                <p><strong>Date:</strong> {new Date(content.createdAt).toLocaleDateString('fr-FR')}</p>
                {content.fileSize && (
                  <p><strong>Taille:</strong> {(content.fileSize / 1024 / 1024).toFixed(2)} MB</p>
                )}
                {content.mimeType && (
                  <p><strong>Type:</strong> {content.mimeType}</p>
                )}
              </div>

              <div style={{ display: 'flex', gap: '1rem' }}>
                <button
                  onClick={() => handleApprove(content.id)}
                  className="btn-primary"
                  disabled={approveMutation.isPending}
                >
                  Approuver
                </button>
                <button
                  onClick={() => setSelectedContent(content)}
                  className="btn-secondary"
                >
                  Rejeter
                </button>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="loading">Aucun contenu en attente de modération</div>
      )}

      {/* Modal de rejet */}
      {selectedContent && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.8)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000,
        }}>
          <div className="card" style={{ maxWidth: '500px', width: '90%' }}>
            <h3>Rejeter le contenu</h3>
            <p><strong>Titre:</strong> {selectedContent.title}</p>
            
            <div className="form-group">
              <label htmlFor="rejectionReason">Raison du rejet :</label>
              <textarea
                id="rejectionReason"
                value={rejectionReason}
                onChange={(e) => setRejectionReason(e.target.value)}
                rows={4}
                placeholder="Expliquez pourquoi ce contenu est rejeté..."
                required
              />
            </div>

            <div style={{ display: 'flex', gap: '1rem', justifyContent: 'flex-end' }}>
              <button
                onClick={() => {
                  setSelectedContent(null)
                  setRejectionReason('')
                }}
                className="btn-secondary"
              >
                Annuler
              </button>
              <button
                onClick={() => handleReject(selectedContent.id)}
                className="btn-primary"
                disabled={rejectMutation.isPending}
              >
                {rejectMutation.isPending ? 'Rejet...' : 'Rejeter'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default ModerationPage
